import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, } from "@/components/ui/select";
import { useToast } from '@/hooks/use-toast';
import { Trash2, Mail, Phone, Calendar, User, MessageSquare } from 'lucide-react';
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5001/api';
const statusColors = {
    new: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
    read: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20',
    replied: 'bg-green-500/10 text-green-500 border-green-500/20',
    archived: 'bg-gray-500/10 text-gray-500 border-gray-500/20',
};
const statusLabels = {
    new: 'Νέο',
    read: 'Αναγνωσμένο',
    replied: 'Απαντημένο',
    archived: 'Αρχειοθετημένο',
};
const ContactManagement = () => {
    const { toast } = useToast();
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all');
    useEffect(() => {
        fetchMessages();
    }, []);
    const fetchMessages = async () => {
        const token = localStorage.getItem('token');
        try {
            const response = await fetch(`${API_URL}/contact`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            const data = await response.json();
            setMessages(data);
        }
        catch (error) {
            toast({
                title: 'Error',
                description: 'Failed to fetch contact messages',
                variant: 'destructive',
            });
        }
        finally {
            setLoading(false);
        }
    };
    const handleStatusChange = async (id, newStatus) => {
        const token = localStorage.getItem('token');
        try {
            await fetch(`${API_URL}/contact/${id}`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ status: newStatus }),
            });
            toast({
                title: 'Success',
                description: 'Message status updated successfully',
            });
            fetchMessages();
        }
        catch (error) {
            toast({
                title: 'Error',
                description: 'Failed to update message status',
                variant: 'destructive',
            });
        }
    };
    const handleDelete = async (id) => {
        if (!confirm('Are you sure you want to delete this message?'))
            return;
        const token = localStorage.getItem('token');
        try {
            await fetch(`${API_URL}/contact/${id}`, {
                method: 'DELETE',
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            toast({
                title: 'Success',
                description: 'Message deleted successfully',
            });
            fetchMessages();
        }
        catch (error) {
            toast({
                title: 'Error',
                description: 'Failed to delete message',
                variant: 'destructive',
            });
        }
    };
    const formatDate = (date) => {
        return new Date(date).toLocaleDateString('el-GR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };
    const filteredMessages = filter === 'all'
        ? messages
        : messages.filter(msg => msg.status === filter);
    const stats = {
        total: messages.length,
        new: messages.filter(m => m.status === 'new').length,
        read: messages.filter(m => m.status === 'read').length,
        replied: messages.filter(m => m.status === 'replied').length,
    };
    if (loading) {
        return (_jsx("div", { className: "flex items-center justify-center h-64", children: _jsx("div", { className: "animate-spin rounded-full h-12 w-12 border-b-2 border-primary" }) }));
    }
    return (_jsxs("div", { className: "space-y-6", children: [_jsx("div", { className: "flex justify-between items-center", children: _jsxs("div", { children: [_jsx("h1", { className: "text-3xl font-bold", children: "Contact Messages" }), _jsx("p", { className: "text-muted-foreground mt-1", children: "Manage contact form submissions" })] }) }), _jsxs("div", { className: "grid grid-cols-1 md:grid-cols-4 gap-4", children: [_jsxs(Card, { children: [_jsx(CardHeader, { className: "pb-3", children: _jsx(CardTitle, { className: "text-sm font-medium text-muted-foreground", children: "Total Messages" }) }), _jsx(CardContent, { children: _jsx("div", { className: "text-3xl font-bold", children: stats.total }) })] }), _jsxs(Card, { children: [_jsx(CardHeader, { className: "pb-3", children: _jsx(CardTitle, { className: "text-sm font-medium text-muted-foreground", children: "New" }) }), _jsx(CardContent, { children: _jsx("div", { className: "text-3xl font-bold text-blue-500", children: stats.new }) })] }), _jsxs(Card, { children: [_jsx(CardHeader, { className: "pb-3", children: _jsx(CardTitle, { className: "text-sm font-medium text-muted-foreground", children: "Read" }) }), _jsx(CardContent, { children: _jsx("div", { className: "text-3xl font-bold text-yellow-500", children: stats.read }) })] }), _jsxs(Card, { children: [_jsx(CardHeader, { className: "pb-3", children: _jsx(CardTitle, { className: "text-sm font-medium text-muted-foreground", children: "Replied" }) }), _jsx(CardContent, { children: _jsx("div", { className: "text-3xl font-bold text-green-500", children: stats.replied }) })] })] }), _jsx("div", { className: "flex gap-2", children: _jsxs(Select, { value: filter, onValueChange: setFilter, children: [_jsx(SelectTrigger, { className: "w-[200px]", children: _jsx(SelectValue, { placeholder: "Filter by status" }) }), _jsxs(SelectContent, { children: [_jsx(SelectItem, { value: "all", children: "All Messages" }), _jsx(SelectItem, { value: "new", children: "New" }), _jsx(SelectItem, { value: "read", children: "Read" }), _jsx(SelectItem, { value: "replied", children: "Replied" }), _jsx(SelectItem, { value: "archived", children: "Archived" })] })] }) }), _jsx("div", { className: "space-y-4", children: filteredMessages.length === 0 ? (_jsx(Card, { children: _jsxs(CardContent, { className: "py-12 text-center", children: [_jsx(MessageSquare, { className: "h-12 w-12 mx-auto mb-4 text-muted-foreground" }), _jsx("p", { className: "text-muted-foreground", children: filter === 'all' ? 'No messages yet' : `No ${filter} messages` })] }) })) : (filteredMessages.map((message) => (_jsxs(Card, { className: "hover:shadow-lg transition-shadow", children: [_jsx(CardHeader, { children: _jsxs("div", { className: "flex justify-between items-start gap-4", children: [_jsxs("div", { className: "flex-1 space-y-2", children: [_jsxs("div", { className: "flex items-center gap-3 flex-wrap", children: [_jsx(CardTitle, { className: "text-xl", children: message.subject }), _jsx(Badge, { className: statusColors[message.status], children: statusLabels[message.status] })] }), _jsxs("div", { className: "flex flex-wrap gap-4 text-sm text-muted-foreground", children: [_jsxs("div", { className: "flex items-center gap-1", children: [_jsx(User, { className: "h-4 w-4" }), _jsx("span", { children: message.name })] }), _jsxs("div", { className: "flex items-center gap-1", children: [_jsx(Mail, { className: "h-4 w-4" }), _jsx("a", { href: `mailto:${message.email}`, className: "hover:text-primary", children: message.email })] }), message.phone && (_jsxs("div", { className: "flex items-center gap-1", children: [_jsx(Phone, { className: "h-4 w-4" }), _jsx("a", { href: `tel:${message.phone}`, className: "hover:text-primary", children: message.phone })] })), _jsxs("div", { className: "flex items-center gap-1", children: [_jsx(Calendar, { className: "h-4 w-4" }), _jsx("span", { children: formatDate(message.createdAt) })] })] })] }), _jsxs("div", { className: "flex gap-2", children: [_jsxs(Select, { value: message.status, onValueChange: (value) => handleStatusChange(message.id, value), children: [_jsx(SelectTrigger, { className: "w-[140px]", children: _jsx(SelectValue, {}) }), _jsxs(SelectContent, { children: [_jsx(SelectItem, { value: "new", children: "\u039D\u03AD\u03BF" }), _jsx(SelectItem, { value: "read", children: "\u0391\u03BD\u03B1\u03B3\u03BD\u03C9\u03C3\u03BC\u03AD\u03BD\u03BF" }), _jsx(SelectItem, { value: "replied", children: "\u0391\u03C0\u03B1\u03BD\u03C4\u03B7\u03BC\u03AD\u03BD\u03BF" }), _jsx(SelectItem, { value: "archived", children: "\u0391\u03C1\u03C7\u03B5\u03B9\u03BF\u03B8\u03B5\u03C4\u03B7\u03BC\u03AD\u03BD\u03BF" })] })] }), _jsx(Button, { size: "sm", variant: "destructive", onClick: () => handleDelete(message.id), children: _jsx(Trash2, { className: "h-4 w-4" }) })] })] }) }), _jsxs(CardContent, { children: [_jsx("div", { className: "bg-muted/50 rounded-lg p-4", children: _jsx("p", { className: "whitespace-pre-wrap text-sm leading-relaxed", children: message.message }) }), _jsxs("div", { className: "mt-4 flex gap-2", children: [_jsxs(Button, { size: "sm", variant: "outline", onClick: () => window.location.href = `mailto:${message.email}?subject=Re: ${message.subject}`, children: [_jsx(Mail, { className: "h-4 w-4 mr-2" }), "Reply via Email"] }), message.phone && (_jsxs(Button, { size: "sm", variant: "outline", onClick: () => window.location.href = `tel:${message.phone}`, children: [_jsx(Phone, { className: "h-4 w-4 mr-2" }), "Call"] }))] })] })] }, message.id)))) })] }));
};
export default ContactManagement;
